// Test Mode data for the per-entity financial stats sections (CLE — supplier /
// client / subscription detail pages). Deterministic 12-month series seeded by
// the entity id so a page renders the same numbers on every visit.
//
// Type-only import from the API layer (anti-cycle rule: testData never imports
// runtime code from lib/).
import type { EntityFinancialStats, EntityStatsTransaction } from '../lib/entityStatsApi';

// 'YYYY-MM' for the month at `offsetFromCurrent` (0 = current, -1 = last…).
function monthLabel(offsetFromCurrent: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetFromCurrent);
  return d.toISOString().slice(0, 7);
}

// Small deterministic hash so amounts vary per entity but stay stable.
function seedOf(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 9973;
  return h;
}

function round2(n: number): number {
  return Number(n.toFixed(2));
}

function buildStats(
  id: string,
  base: number,
  direction: 'IN' | 'OUT',
  source: EntityStatsTransaction['source'],
  descriptionPrefix: string,
): EntityFinancialStats {
  const seed = seedOf(id);
  const monthly = Array.from({ length: 12 }, (_, i) => {
    const offset = i - 11; // oldest first
    const amount = round2(base + ((seed + i * 7) % 10) * (base / 20));
    return { month: monthLabel(offset), amount };
  });

  const transactions: EntityStatsTransaction[] = monthly
    .map((m, i) => ({
      id: `${id}-tx-${i}`,
      date: `${m.month}-15T10:00:00.000Z`,
      description: `${descriptionPrefix} ${m.month}`,
      amount: m.amount,
      currency: 'RON',
      direction,
      source,
      paymentMethod: source === 'bank' ? ((seed + i) % 2 === 0 ? 'CARD' : 'BANK_TRANSFER') : null,
      status: source === 'bank' ? null : 'PAID',
      invoiceNumber: source === 'bank' ? null : `${id.slice(0, 3).toUpperCase()}-${100 + i}`,
    }))
    .reverse(); // desc by date

  const total = monthly.reduce((s, m) => s + m.amount, 0);
  const last = transactions[0];

  return {
    currency: 'RON',
    totals: {
      totalAmount: round2(total),
      transactionCount: transactions.length,
      monthlyAverage: round2(total / 12),
      lastPayment: last ? { date: last.date, amount: last.amount, currency: last.currency } : null,
    },
    monthly,
    transactions,
  };
}

export function buildSubscriptionStats(id: string): EntityFinancialStats {
  return buildStats(id, 250, 'OUT', 'bank', 'Plată POS');
}

export function buildSupplierStats(id: string): EntityFinancialStats {
  return buildStats(id, 1800, 'OUT', 'invoice', 'Factură furnizor');
}

export function buildClientStats(id: string): EntityFinancialStats {
  return buildStats(id, 8500, 'IN', 'smartbill', 'Factură emisă');
}
